import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    Typography,
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    IconButton,
    useTheme,
    useMediaQuery,
    Stack,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Rating,
    Tooltip,
    InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import StarIcon from '@mui/icons-material/Star';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArticleIcon from '@mui/icons-material/Article';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ColorData from 'Components/CstmTheme/ColorData';
import AdminApi from 'Service/Admin.api';
import { useDispatch } from 'react-redux';
import { setAlertData, startLoading, stopLoading } from '../../Redux/Screen/Action';
import { Document, VerifyProfessionalDialogProps } from './type';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: theme.spacing(2),
        maxWidth: '90vw',
        width: '100%',
        maxHeight: '90vh',
        [theme.breakpoints.up('sm')]: {
            maxWidth: '800px',
        },
        [theme.breakpoints.up('md')]: {
            maxWidth: '900px',
        },
    },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2, 3),
    position: 'relative',
    '& .MuiTypography-root': {
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
}));

const InfoCard = styled(Card)(({ theme }) => ({
    height: '100%',
    transition: 'all 0.3s ease',
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
    },
}));



const DocumentCard = styled(Card)(({ theme }) => ({
    height: '100%',
    transition: 'all 0.3s ease',
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8],
        borderColor: theme.palette.primary.main,
    },
}));

const DocumentPreview = styled(Box)(({ theme }) => ({
    height: 120,
    background: `linear-gradient(135deg, ${theme.palette.grey[100]} 0%, ${theme.palette.grey[200]} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: `${theme.spacing(1)} ${theme.spacing(1)} 0 0`,
    position: 'relative',
    overflow: 'hidden',
}));

const DocumentActions = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(0.5),
    opacity: 0,
    transition: 'opacity 0.3s ease',
    '.MuiCard-root:hover &': {
        opacity: 1,
    },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[3],
    border: `1px solid ${theme.palette.divider}`,

    // Hide horizontal overflow
    overflowX: 'hidden',

    // Firefox
    scrollbarWidth: 'none',

    // Chrome, Safari, Edge
    '&::-webkit-scrollbar': {
        display: 'none',
    },

    // Ensure min width does not cause scrollbars
    '& .MuiTable-root': {
        minWidth: 650,
        tableLayout: 'fixed', // Prevents width expansion
        width: '100%',
    },

    // Define column widths to prevent overflow
    '& .MuiTableCell-root': {
        // '&:nth-of-type(1)': { width: '15%' }, // Skill Name
        // '&:nth-of-type(2)': { width: '12%' }, // Experience
        // '&:nth-of-type(3)': { width: '12%' }, // Rating
        // '&:nth-of-type(4)': { width: '15%' }, // Service Area
        // '&:nth-of-type(5)': { width: '25%' }, // Description
        '&:nth-of-type(6)': { width: '21%' }, // Professional Fees
    },
}));


const StyledTableHead = styled(TableHead)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    '& .MuiTableCell-head': {
        color: theme.palette.primary.contrastText,
        fontWeight: 600,
        fontSize: '0.95rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: 'none',
        padding: theme.spacing(1),
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    transition: 'all 0.3s ease',
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
        backgroundColor: theme.palette.primary.light + '15',
        transform: 'scale(1.01)',
        boxShadow: theme.shadows[2],
    },
    '& .MuiTableCell-root': {
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1.5),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '&:last-child': {
            whiteSpace: 'normal', // Allow wrapping for fees column
        },
    },
}));

const SkillCard = styled(Card)(({ theme }) => ({
    height: '100%',
    transition: 'all 0.3s ease',
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8],
        borderColor: theme.palette.primary.main,
    },
}));

const FeesInputContainer = styled(Box)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.success.light}10 0%, ${theme.palette.success.main}05 100%)`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.5),
    border: `1px solid ${theme.palette.success.main}30`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
}));


const VerifyProfessionalDialog: React.FC<VerifyProfessionalDialogProps> = ({
    open,
    onClose,
    user,
    onVerify,
    fetchListData
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
    const dispatch = useDispatch()

    useEffect(() => {
        setViewMode(isMobile ? 'grid' : 'table');
    }, [isMobile]);

    // Initialize skills with fees
    const initialSkillsWithFees = user.skills?.map(skill => ({
        ...skill,
        fees: skill.professionalFeesPerCallingRs || 300
    })) || [];
    const [skillsWithFees, setSkillsWithFees] = useState(initialSkillsWithFees);


    // Get documents from user or use demo data
    const documents =
        user?.certificates?.map((cert, index) => {
            const fileUrl = cert.file || '';
            const extension = fileUrl.split('.').pop()?.toLowerCase() || '';
            const type = extension === 'pdf' ? 'pdf' : 'image';

            return {
                id: index + 1,
                name: `Certificate ${index + 1}`,
                type,
                url: fileUrl,
                uploadDate: '2024-01-15', // hardcoded as required
                size: '2 MB', // dummy size
            };
        }) || [];


    const handleVerify = async () => {
        try {
            // ✅ Basic validation
            if (!user?.email || !user?.id) {
                console.warn("User data is incomplete");
                return;
            }
            dispatch(startLoading({
                loading: true,
                loadtext: "Fetching Professional User Data...",
            }));

            // ✅ Prepare skill fee data
            const skillFees = skillsWithFees.map(({ pkSkillProfessionalMapping, fees }) => ({
                skillId: pkSkillProfessionalMapping,
                fees: fees || 0,
            }));

            // ✅ API call
            const response = await AdminApi.VerifyProfessional({
                email: user.email,
                userType: "PROFESSIONAL",
                skillFees,
            });

            // ✅ Handle response
            if (response?.success && response.alert) {
                dispatch(stopLoading());
                dispatch(setAlertData({
                    alertMessage: response.alert.alertMessage,
                    alertVariant: response.alert.alertVariant,
                    openAlert: response.alert.openAlert,
                }));
                fetchListData();
            } else {
                console.warn("Verification failed or no alert returned");
            }
        } catch (error) {
            console.error("Error verifying professional:", error);
        } finally {
            onClose(); // Always close the modal/dialog
        }
    };


    const handleSkillFeesChange = (skillName: string, fees: number) => {
        setSkillsWithFees(prev =>
            prev.map(skill =>
                skill.skillName === skillName
                    ? { ...skill, fees }
                    : skill
            )
        );
    };

   const handleDocumentView = (document: Document) => {
    const baseUrl = process.env.REACT_APP_BASE_URL; // Make sure this is defined in your `.env` file
    const documentUrl = `${baseUrl}${document.url}`;
    window.open(documentUrl, '_blank');
};

const handleDocumentDownload = async (doc: Document) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const documentUrl = `${baseUrl}${doc.url}`;

  try {
    const response = await fetch(documentUrl, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch document');
    }

    const blob = await response.blob();

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a'); // ✅ now uses the global DOM document
    link.href = blobUrl;
    link.download = doc.name || 'document.html';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading document:', error);
  }
};



    const getDocumentIcon = (type: string) => {
        switch (type.toLowerCase()) {
            case 'pdf':
                return <PictureAsPdfIcon sx={{ fontSize: 40, color: '#d32f2f' }} />;
            case 'image':
            case 'jpg':
            case 'jpeg':
            case 'png':
                return <ImageIcon sx={{ fontSize: 40, color: '#1976d2' }} />;
            default:
                return <ArticleIcon sx={{ fontSize: 40, color: '#757575' }} />;
        }
    };

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
        >
            <StyledDialogTitle>
                {/* <VerifiedIcon sx={{ fontSize: { xs: 24, sm: 28 } }} /> */}
                <Typography variant={isMobile ? "h6" : "h5"} component="div" color='white'>
                    <VerifiedIcon sx={{ fontSize: { xs: 24, sm: 28, color: 'white' } }} />
                    Verify Professional Skills and Fees Per Case
                </Typography>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'inherit',
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </StyledDialogTitle>

            <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Professional Info Header */}
                <Box sx={{ mb: 3 }}>
                    <Card elevation={2} sx={{ mb: 3 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={2} mb={2}>
                                <Avatar
                                    sx={{
                                        bgcolor: theme.palette.primary.main,
                                        width: { xs: 50, sm: 60 },
                                        height: { xs: 50, sm: 60 }
                                    }}
                                >
                                    {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        {user.firstName} {user.lastName}
                                    </Typography>
                                    <Typography variant="body2" color={ColorData.text.primary}>
                                        {user.email}
                                    </Typography>
                                    <Typography variant="body2" color={ColorData.text.primary}>
                                        {user.contactNumber}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                <Grid container spacing={3}>
                    {/* Skills & Professional Fees Section */}
                    {/* currently commented out because no need to show pricing */}
                    {false && (
                    <Grid>
                        <InfoCard elevation={2}>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <StarIcon color="primary" />
                                        <Typography variant="h6" fontWeight="bold">
                                            Professional Skills & Fees
                                        </Typography>
                                        <Chip
                                            label={`${skillsWithFees.length} Skills`}
                                            color="primary"
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>

                                    {/* View Toggle Buttons */}
                                    {!isMobile && (
                                        <Box display="flex" gap={1}>
                                            <Tooltip title="Table View">
                                                <IconButton
                                                    onClick={() => setViewMode('table')}
                                                    color={viewMode === 'table' ? 'primary' : 'default'}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: viewMode === 'table' ? theme.palette.primary.light + '20' : 'transparent',
                                                    }}
                                                >
                                                    <ViewListIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Grid View">
                                                <IconButton
                                                    onClick={() => setViewMode('grid')}
                                                    color={viewMode === 'grid' ? 'primary' : 'default'}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: viewMode === 'grid' ? theme.palette.primary.light + '20' : 'transparent',
                                                    }}
                                                >
                                                    <ViewModuleIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Box>

                                {/* Table View */}

                                {viewMode === 'table' && (
                                    <StyledTableContainer sx={{ maxHeight: 600, overflow: 'visible' }} >
                                        <Table>
                                            <StyledTableHead>
                                                <TableRow>
                                                    <TableCell>Skill Name</TableCell>
                                                    <TableCell align="center">Experience</TableCell>
                                                    <TableCell align="center">Rating</TableCell>
                                                    <TableCell align="center">Service Area</TableCell>
                                                    <TableCell>Description</TableCell>
                                                    <TableCell align="center">Professional Fees (₹)</TableCell>
                                                </TableRow>
                                            </StyledTableHead>
                                            <TableBody sx={{ overflow: 'hidden', height: '100%' }}>
                                                {skillsWithFees.map((skill, index) => (
                                                    <StyledTableRow key={index}>
                                                        <TableCell>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <WorkIcon color="primary" fontSize="small" />
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    fontWeight="bold"
                                                                    color="primary"
                                                                >
                                                                    {skill.skillName}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Chip
                                                                label={`${skill.numberOfExperienceYears} years`}
                                                                size="small"
                                                                color="success"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Rating
                                                                value={skill.rating || 0}
                                                                readOnly
                                                                size="small"
                                                                precision={0.5}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                                <LocationOnIcon fontSize="small" color="action" />
                                                                <Typography variant="body2" color={ColorData.text.primary}>
                                                                    {skill.servesPanIndia ? 'Pan India' :
                                                                        skill.servesStates ?
                                                                            skill.servesStates.split(',').slice(0, 1).join(', ') +
                                                                            (skill.servesStates.split(',').length > 1 ? '...' : '') :
                                                                            'Local'}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ whiteSpace: 'normal !important' }}>
                                                            <Typography
                                                                variant="body2"
                                                                color={ColorData.text.primary}
                                                                sx={{
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    overflow: 'hidden',
                                                                    lineHeight: 1.4,
                                                                    wordBreak: 'break-word',
                                                                }}
                                                                title={skill.description}
                                                            >
                                                                {skill.description || 'No description'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ padding: theme.spacing(1) }}>
                                                            <FeesInputContainer>
                                                                <TextField
                                                                    type="number"
                                                                    value={skill.fees}
                                                                    onChange={(e) => handleSkillFeesChange(
                                                                        skill.skillName,
                                                                        parseInt(e.target.value) || 0
                                                                    )}
                                                                    slotProps={{
                                                                        input: {
                                                                            startAdornment: (
                                                                                <InputAdornment position="start">
                                                                                    <CurrencyRupeeIcon
                                                                                        color="success"
                                                                                        sx={{ fontSize: 16 }}
                                                                                    />
                                                                                </InputAdornment>
                                                                            ),
                                                                        }
                                                                    }}
                                                                    size="small"
                                                                    sx={{
                                                                        width: '100%',
                                                                        maxWidth: '100px',
                                                                        minWidth: '80px',
                                                                        '& .MuiOutlinedInput-root': {
                                                                            backgroundColor: 'white',
                                                                            fontSize: '0.875rem',
                                                                            '& input': {
                                                                                padding: theme.spacing(0.75, 0.5),
                                                                                textAlign: 'center',
                                                                            },
                                                                            '&:hover fieldset': {
                                                                                borderColor: theme.palette.success.main,
                                                                            },
                                                                            '&.Mui-focused fieldset': {
                                                                                borderColor: theme.palette.success.main,
                                                                            },
                                                                        },
                                                                        '& .MuiInputAdornment-root': {
                                                                            marginLeft: 0,
                                                                            marginRight: theme.spacing(0.5),
                                                                        },
                                                                    }}
                                                                />
                                                            </FeesInputContainer>
                                                        </TableCell>
                                                    </StyledTableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </StyledTableContainer>
                                )}

                                {/* Grid View */}
                                {viewMode === 'grid' && (
                                    <Grid container spacing={3}>
                                        {skillsWithFees.map((skill, index) => (
                                            <Grid key={index} >
                                                <SkillCard elevation={3}>
                                                    <CardContent sx={{ p: 3 }}>
                                                        {/* Skill Name */}
                                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                                            <WorkIcon color="primary" fontSize="small" />
                                                            <Typography
                                                                variant="h6"
                                                                fontWeight="bold"
                                                                color="primary"
                                                                noWrap
                                                                title={skill.skillName}
                                                            >
                                                                {skill.skillName}
                                                            </Typography>
                                                        </Box>

                                                        {/* Rating */}
                                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                                            <Typography variant="body2" color={ColorData.text.primary}>
                                                                Rating:
                                                            </Typography>
                                                            <Rating
                                                                value={skill.rating || 0}
                                                                readOnly
                                                                size="small"
                                                                precision={0.5}
                                                            />
                                                        </Box>

                                                        {/* Experience */}
                                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                                            <Typography variant="body2" color={ColorData.text.primary}>
                                                                Experience:
                                                            </Typography>
                                                            <Chip
                                                                label={`${skill.numberOfExperienceYears} years`}
                                                                size="small"
                                                                color="success"
                                                                variant="outlined"
                                                            />
                                                        </Box>

                                                        {/* Service Area */}
                                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                                            <LocationOnIcon fontSize="small" color="action" />
                                                            <Typography variant="body2" color={ColorData.text.primary}>
                                                                {skill.servesPanIndia ? 'Pan India' :
                                                                    skill.servesStates ?
                                                                        skill.servesStates.split(',').slice(0, 2).join(', ') +
                                                                        (skill.servesStates.split(',').length > 2 ? '...' : '') :
                                                                        'Local'}
                                                            </Typography>
                                                        </Box>

                                                        {/* Description */}
                                                        {skill.description && (
                                                            <Box mb={3}>
                                                                <Typography
                                                                    variant="body2"
                                                                    color={ColorData.text.primary}
                                                                    sx={{
                                                                        display: '-webkit-box',
                                                                        WebkitLineClamp: 3,
                                                                        WebkitBoxOrient: 'vertical',
                                                                        overflow: 'hidden',
                                                                        lineHeight: 1.5
                                                                    }}
                                                                    title={skill.description}
                                                                >
                                                                    {skill.description}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {/* Professional Fees Input */}
                                                        <FeesInputContainer>
                                                            <Typography variant="subtitle2" fontWeight="bold" color="success.main" mb={1}>
                                                                Professional Fees
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                type="number"
                                                                value={skill.fees}
                                                                onChange={(e) => handleSkillFeesChange(
                                                                    skill.skillName,
                                                                    parseInt(e.target.value) || 0
                                                                )}
                                                                slotProps={{
                                                                    input: {
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                <CurrencyRupeeIcon
                                                                                    color="success"
                                                                                    sx={{ fontSize: 18 }}
                                                                                />
                                                                            </InputAdornment>
                                                                        ),
                                                                    }
                                                                }}
                                                                size="small"
                                                                sx={{
                                                                    width: '12.5rem',        // Fixed width (200px)
                                                                    maxWidth: '100%',        // Responsive limit
                                                                    overflow: 'hidden',      // Prevent overflow
                                                                    textOverflow: 'ellipsis',// Truncate if long
                                                                    whiteSpace: 'nowrap',
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '&:hover fieldset': {
                                                                            borderColor: theme.palette.success.main,
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: theme.palette.success.main,
                                                                        },
                                                                    },
                                                                }}
                                                            />
                                                        </FeesInputContainer>
                                                    </CardContent>
                                                </SkillCard>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}

                                {skillsWithFees.length === 0 && (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        py={6}
                                        color="text.secondary"
                                    >
                                        <StarIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                                        <Typography variant="h6" fontWeight="bold" mb={1}>
                                            No skills added yet
                                        </Typography>
                                        <Typography variant="body2">
                                            Professional skills will appear here once added
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </InfoCard>
                    </Grid>
                    )}

                    {/* User Roles */}
                    {/* <Grid  >
                        <InfoCard elevation={2}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <WorkIcon color="primary" />
                                    <Typography variant="h6" fontWeight="bold">
                                        Professional Roles
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                    {user.roles?.map((role: string, index: number) => (
                                        <Chip
                                            key={index}
                                            label={role}
                                            color="secondary"
                                            variant="filled"
                                            size={isMobile ? "small" : "medium"}
                                        />
                                    ))}
                                </Stack>
                            </CardContent>
                        </InfoCard>
                    </Grid> */}

                    {/* Qualification */}
                    {/* <Grid >
                        <InfoCard elevation={2}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <DescriptionIcon color="primary" />
                                    <Typography variant="h6" fontWeight="bold">
                                        Highest Qualification
                                    </Typography>
                                </Box>
                                <Typography variant="h6" color="primary" fontWeight="bold">
                                    {user.highestQualification || 'Not specified'}
                                </Typography>
                            </CardContent>
                        </InfoCard>
                    </Grid> */}

                    {/* Documents & Certificates */}
                    <Grid >
                        <InfoCard elevation={2}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={3}>
                                    <ArticleIcon color="primary" />
                                    <Typography variant="h6" fontWeight="bold">
                                        Documents & Certificates
                                    </Typography>
                                    <Chip
                                        label={`${documents.length} Documents`}
                                        color="primary"
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>

                                <Grid container spacing={2}>
                                    {documents.map((document: Document) => (
                                        <Grid key={document.id}>
                                            <DocumentCard elevation={2}>
                                                <DocumentPreview>
                                                    {getDocumentIcon(document.type)}
                                                    <DocumentActions>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDocumentView(document);
                                                            }}
                                                            sx={{
                                                                bgcolor: 'rgba(255,255,255,0.9)',
                                                                '&:hover': { bgcolor: 'white' }
                                                            }}
                                                        >
                                                            <VisibilityIcon fontSize="small" color="primary" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDocumentDownload(document);
                                                            }}
                                                            sx={{
                                                                bgcolor: 'rgba(255,255,255,0.9)',
                                                                '&:hover': { bgcolor: 'white' }
                                                            }}
                                                        >
                                                            <DownloadIcon fontSize="small" color="success" />
                                                        </IconButton>
                                                    </DocumentActions>
                                                </DocumentPreview>
                                                <CardContent sx={{ p: 2 }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        fontWeight="bold"
                                                        noWrap
                                                        title={document.name}
                                                    >
                                                        {document.name}
                                                    </Typography>
                                                    <Typography variant="caption" color={ColorData.text.primary} display="block">
                                                        {document.size}
                                                    </Typography>
                                                    <Typography variant="caption" color={ColorData.text.primary} display="block">
                                                        Uploaded: {new Date(document.uploadDate).toLocaleDateString()}
                                                    </Typography>
                                                    <Box mt={1} display="flex" gap={1}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<VisibilityIcon />}
                                                            onClick={() => handleDocumentView(document)}
                                                            sx={{ fontSize: '0.7rem', py: 0.5 }}
                                                        >
                                                            View
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            startIcon={<DownloadIcon />}
                                                            onClick={() => handleDocumentDownload(document)}
                                                            sx={{ fontSize: '0.7rem', py: 0.5 }}
                                                        >
                                                            Download
                                                        </Button>
                                                    </Box>
                                                </CardContent>
                                            </DocumentCard>
                                        </Grid>
                                    ))}
                                </Grid>

                                {documents.length === 0 && (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        py={4}
                                        color="text.secondary"
                                    >
                                        <ArticleIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                                        <Typography variant="body1">
                                            No documents uploaded yet
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </InfoCard>
                    </Grid>
                </Grid>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    size={isMobile ? "medium" : "large"}
                    sx={{ minWidth: { xs: 100, sm: 120 } }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleVerify}
                    variant="contained"
                    color="primary"
                    startIcon={<VerifiedIcon />}
                    size={isMobile ? "medium" : "large"}
                    sx={{ minWidth: { xs: 120, sm: 140 } }}
                >
                    Verify Professional
                </Button>
            </DialogActions>
        </StyledDialog >
    );
};

export default VerifyProfessionalDialog;